/**
 * Data access for the public leaderboard surface (/leaderboard*, /i/:code) — Slice 3 of
 * .claude/contracts/referral-leaderboard-program.md. All response shapes are snake_case (D8) and
 * match the contract's "API Contract" section field-for-field. These endpoints are public (no
 * auth) and go through the general `/api/` proxy — unlike ReferralMetricService.ts's authenticated
 * calls, there's no Bearer token to protect here, so the `/api/referral-metric-service/` location
 * added for those still works fine (it simply has no Authorization header to preserve).
 */
import ApiService from '../../infrastructure/api/ApiService';

export type LeaderboardScope = 'GLOBAL' | 'COUNTRY' | 'CITY';

export interface BadgeSummary {
  code: string;
  icon: string;
}

export interface LeaderboardEntry {
  rank: number;
  user_id: number;
  display_name: string | null;
  avatar_url: string | null;
  score: number;
  activated_referrals: number;
  verified_attendees: number;
  badges: BadgeSummary[];
}

export interface PublicLeaderboardResponse {
  scope: LeaderboardScope;
  scope_id: number | null;
  scope_name: string | null;
  generated_at: string | null;
  total_elements: number;
  entries: LeaderboardEntry[];
}

export interface CountryScope {
  id: number;
  name: string;
  code: string;
}

export interface CityScope {
  id: number;
  name: string;
  country_id: number;
}

export interface LeaderboardScopesResponse {
  countries: CountryScope[];
  cities: CityScope[];
}

export interface BadgeCatalogueEntry {
  code: string;
  name: string;
  icon: string;
  seasonal: boolean;
}

const BASE = '/referral-metric-service/api/v1/public';
const PAGE_SIZE = 25;

class LeaderboardService {
  async getLeaderboard(scope: LeaderboardScope, scopeId?: number, page = 0): Promise<PublicLeaderboardResponse> {
    const params: Record<string, string | number> = { scope, page, size: PAGE_SIZE };
    if (scopeId !== undefined) {
      params.scopeId = scopeId;
    }
    const response = await ApiService.get<PublicLeaderboardResponse>(`${BASE}/leaderboard`, params);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load the leaderboard');
    }
    return response.data;
  }

  async getScopes(): Promise<LeaderboardScopesResponse> {
    const response = await ApiService.get<LeaderboardScopesResponse>(`${BASE}/leaderboard/scopes`);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load leaderboard regions');
    }
    return response.data;
  }

  async getBadgeCatalogue(): Promise<BadgeCatalogueEntry[]> {
    const response = await ApiService.get<BadgeCatalogueEntry[]>(`${BASE}/badges`);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load the badge catalogue');
    }
    return response.data;
  }
}

const leaderboardService = new LeaderboardService();
export default leaderboardService;
